/**
 * Access control — the provider.
 *
 * Resolves the `AccessSubject` once and hands it to the whole tree. Mounted in
 * `src/pages/[org_id]/layout.tsx`, inside the org route so `org_id` is in the
 * URL and the org-scoped queries (role, subscription, Square, providers) can
 * actually run.
 *
 * Why a provider and not a plain hook at each call site: resolving the subject
 * costs several RTK queries, and — the real reason — a context assembled twice
 * drifts. The two `NavContext` builders already demonstrated that; a gate added
 * to one silently closed the item on the other. One subject, one tree.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { can, cannot, grantedCapabilities } from './access.policy'
import {
  EMPTY_SUBJECT,
  type AccessSubject,
  type Capability,
} from './access.types'
import { useAccessSubject } from './use-access-subject'

export interface AccessApi {
  /** Does the current user hold this capability? */
  can: (capability: Capability) => boolean
  /** Readability inverse of `can`, for early-return guards. */
  cannot: (capability: Capability) => boolean
  /** The resolved subject. Read `isResolved` before rendering an empty state. */
  subject: AccessSubject
  /** Everything the current user holds. Debugging and the staff inspector. */
  granted: () => Capability[]
}

const FALLBACK: AccessApi = {
  can: () => false,
  cannot: () => true,
  subject: EMPTY_SUBJECT,
  granted: () => [],
}

const AccessContext = createContext<AccessApi>(FALLBACK)

export function AccessProvider({ children }: { children: ReactNode }) {
  const subject = useAccessSubject()

  const value = useMemo<AccessApi>(
    () => ({
      can: (capability: Capability) => can(subject, capability),
      cannot: (capability: Capability) => cannot(subject, capability),
      subject,
      granted: () => grantedCapabilities(subject),
    }),
    [subject]
  )

  return (
    <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
  )
}

/**
 * Ask what the current user can do.
 *
 * ```tsx
 * const { can } = useAccess()
 * return can('bookings.waitlist') ? <WaitlistButton /> : null
 * ```
 *
 * Outside `AccessProvider` this returns the fail-closed fallback: every
 * capability is denied. That is deliberate — a component rendered somewhere the
 * provider does not reach should hide the guarded thing, not reveal it.
 */
export function useAccess(): AccessApi {
  return useContext(AccessContext)
}

/**
 * Single-capability convenience for the common one-liner.
 *
 * ```tsx
 * const canEditLayout = useCan('bookings.floorplan.edit')
 * ```
 */
export function useCan(capability: Capability): boolean {
  return useAccess().can(capability)
}
